import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';

import type { SpGroupType } from '@/hooks/swr/group/useSPGroups';
import useSPGroups from '@/hooks/swr/group/useSPGroups';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';

import type { NotificationContentProps } from '@/utils/hooks/context/toast/types';

import Notification from '@/components/base/Notification';

import { CONTENT_ALBUM_POSTS } from '@/routes/privateRoutes';

const NewContentAutomaticGroupAlbum = ({ notification }: NotificationContentProps) => {
    const router = useRouter();
    const albumId = notification?.data?.album_id;
    const groupId = notification?.data?.group_id as SpGroupType['id'];
    const { spGroups } = useSPGroups({ revalidateIfStale: false });
    const { setActiveGroup } = useActiveGroup();

    const selectedGroup = useMemo(
        () => spGroups?.find((group) => group.id === groupId),
        [groupId, spGroups]
    );

    const url = albumId ? CONTENT_ALBUM_POSTS.replace('[albumId]', albumId.toString()) : null;

    useEffect(() => {
        if (url) {
            router.prefetch(url);
        }
    }, [router, url]);

    return (
        <Notification
            dataNotification={notification}
            onClick={() => {
                if (selectedGroup) {
                    setActiveGroup(selectedGroup);
                }
                if (url) {
                    router.push(url);
                }
            }}
            contentHidden={notification?.options?.hideContent ?? false}
            wide={notification?.options?.wide ?? true}>
            <Notification.GroupAvatar group={selectedGroup} />
            <Notification.Content>
                <Notification.Title i18nKey="commons:notifications.new_content_automatic_group_album.title" />
                <Notification.Description
                    i18nKey="commons:notifications.new_content_automatic_group_album.description"
                    values={{
                        album_name: notification?.data?.album_name ?? ''
                    }}
                />
            </Notification.Content>
        </Notification>
    );
};

export default NewContentAutomaticGroupAlbum;
